// mock-data.js — backward-compatible facade over the rich JSON dataset.
//
// All 34 pages still import from here. Under the hood we now read from
// /lib/store.js (which is hydrated from /src/data/*.json and persists
// mutations to localStorage). The arrays exported below are STABLE
// references that the store mutates in place, so existing pages stay live
// without any refactor.

import { STORE, mutate, appendAuditEvent, resetStore } from "./store";
import { canPerform, PermissionDeniedError } from "./permissions";

// ── Defensive permission guard for admin mutators ─────────────────────────
// Belt-and-suspenders behind <Gated>: if a click handler forgets the wrapper
// or if a mutator is called from the browser console, we still block the
// action AND drop a PermissionDenied event into the audit log so the trail is
// complete. Reads the current admin role from the SUPER_ADMIN proxy at the
// time of the call, so it always sees the latest pinned identity.
function guardAction(action) {
  // Resolve the current admin id + role through the manifest pin so this
  // works even if SUPER_ADMIN's Proxy isn't initialized yet (early-module
  // calls during hydration).
  const adminId = STORE.manifest?.adminUsers?.SUPER_ADMIN || "adm_001";
  const adminRec = STORE.adminUsers.find(a => a.id === adminId);
  const role = adminRec?.role || null;
  if (canPerform(action, role)) return;
  // Log the denial to the audit trail before throwing.
  mutate(draft => {
    appendAuditEvent(draft, {
      action:       "PermissionDenied",
      entityType:   "Permission",
      entityId:     action,
      description:  `Blocked ${action} attempt — role ${role || "anonymous"} not authorized`,
      actorId:      adminId,
      actorRole:    role || "anonymous",
    });
  });
  throw new PermissionDeniedError(action, role);
}

// ─── Stable live entity arrays ──────────────────────────────────────────────
// Splice-in-place mutations in store.js keep the references intact.
export const brands           = STORE.brands;
export const deals            = STORE.deals;
export const members          = STORE.members;
export const applications     = STORE.applications;
export const reservations     = STORE.reservations;
export const submissions      = STORE.submissions;
export const cards            = STORE.cards;
export const paymentItems     = STORE.paymentItems;
export const paymentRuns      = STORE.paymentRuns;
export const auditEvents      = STORE.auditEvents;
export const validationRules  = STORE.validationRules;
export const adminUsers       = STORE.adminUsers;

// ─── Manifest-pinned demo records (live-resolved via getter wrappers) ───────
function pin(entity, key) {
  const id = STORE.manifest?.[entity]?.[key];
  if (!id) return null;
  return STORE[entity].find(r => r.id === id) || null;
}

// Object-proxy wrappers so `DEMO_MEMBER.firstName` reads the latest pinned
// record (handy if a mutation changes their data).
const livePin = (entity, key) => new Proxy({}, {
  get: (_, prop) => {
    const rec = pin(entity, key);
    if (!rec) return undefined;
    // For function-valued props (rare), bind to record; otherwise return raw.
    const v = rec[prop];
    return typeof v === "function" ? v.bind(rec) : v;
  },
  has: (_, prop) => { const rec = pin(entity, key); return rec ? prop in rec : false; },
  ownKeys: () => { const rec = pin(entity, key); return rec ? Object.keys(rec) : []; },
  getOwnPropertyDescriptor: (_, prop) => {
    const rec = pin(entity, key);
    if (!rec || !(prop in rec)) return undefined;
    return { configurable: true, enumerable: true, value: rec[prop], writable: false };
  },
});

export const DEMO_MEMBER               = livePin("members", "DEMO_MEMBER");
export const MEMBER_NO_HISTORY         = livePin("members", "MEMBER_NO_HISTORY");
export const MEMBER_RETURNED_PAYMENTS  = livePin("members", "MEMBER_RETURNED_PAYMENTS");
export const MEMBER_SUSPENDED_FRAUD    = livePin("members", "MEMBER_SUSPENDED_FRAUD");

// ── Current-user session ──────────────────────────────────────────────────
// `DEMO_MEMBER` is the proxy every member-facing page reads from. We re-pin
// it under the hood so a real onboarded user sees their OWN data (which will
// be empty), not Jennifer's. Switching back to a demo persona repins to
// the original mbr_001 from the manifest.
const DEFAULT_DEMO_MEMBER_ID = "mbr_001";

export function getCurrentMemberId() {
  return STORE.manifest?.members?.DEMO_MEMBER || DEFAULT_DEMO_MEMBER_ID;
}

export function isRealUserSignedIn() {
  const id = getCurrentMemberId();
  // Real onboarded users get IDs prefixed `mbr_` followed by hex (from backend).
  // Mocked seeded members use `mbr_001` … `mbr_xxx` pattern. We distinguish by
  // checking the member record's `onboardedLive` flag we set on signIn.
  const rec = STORE.members.find(m => m.id === id);
  return !!rec?.onboardedLive;
}

export function signInAsMember(memberDoc) {
  // memberDoc = { memberId, firstName, lastName, email, phone, address, memberSince }
  if (!memberDoc?.memberId) throw new Error("signInAsMember: memberId required");
  mutate(draft => {
    // Upsert the member record so every store-driven page picks them up.
    const existingIdx = draft.members.findIndex(m => m.id === memberDoc.memberId);
    const record = {
      id:           memberDoc.memberId,
      firstName:    memberDoc.firstName,
      lastName:     memberDoc.lastName,
      email:        memberDoc.email,
      phone:        memberDoc.phone || "",
      address:      memberDoc.address || null,
      status:       "Active",
      memberSince:  memberDoc.memberSince || new Date().toISOString(),
      // Capacity defaults: a new member starts with $50k of unlocked capacity
      // (matches the prototype's seeded values for new accounts).
      capacityLimit:     50000,
      capacityUsed:      0,
      capacityAvailable: 50000,
      // Empty payout history.
      lifetimePayoutsUSD: 0,
      totalCardsSubmitted: 0,
      onboardedLive: true,    // flag distinguishing real users from seed demos
    };
    if (existingIdx >= 0) draft.members[existingIdx] = { ...draft.members[existingIdx], ...record };
    else draft.members.push(record);
    // Re-pin DEMO_MEMBER so all member-shell reads now flow through this user.
    draft.manifest = { ...draft.manifest, members: { ...draft.manifest.members, DEMO_MEMBER: memberDoc.memberId } };
    appendAuditEvent(draft, {
      action:      "MemberSignedIn",
      entityType:  "Member",
      entityId:    memberDoc.memberId,
      description: `Real user signed in: ${memberDoc.email}`,
      actorId:     memberDoc.memberId,
    });
  });
}

export function signOutMember() {
  mutate(draft => {
    draft.manifest = { ...draft.manifest, members: { ...draft.manifest.members, DEMO_MEMBER: DEFAULT_DEMO_MEMBER_ID } };
  });
}

export function setDemoPersona(memberId) {
  // Used by the demo persona switcher on the login page.
  mutate(draft => {
    draft.manifest = { ...draft.manifest, members: { ...draft.manifest.members, DEMO_MEMBER: memberId } };
  });
}

export const DEAL_NEAR_CAP        = livePin("deals", "DEAL_NEAR_CAP");
export const DEAL_URGENT          = livePin("deals", "DEAL_URGENT");
export const DEAL_RATE_CHANGED    = livePin("deals", "DEAL_RATE_CHANGED");

export const APP_KYC_FAILED  = livePin("applications", "APP_KYC_FAILED");
export const APP_REJECTED    = livePin("applications", "APP_REJECTED");

export const ADM08_DETAIL_SUBMISSION = livePin("submissions", "ADM08_DETAIL_SUBMISSION");
export const ADM10_DETAIL_RUN        = livePin("paymentRuns", "ADM10_DETAIL_RUN");
export const RUN_WITH_RETURNED       = livePin("paymentRuns", "RUN_WITH_RETURNED");
export const RUN_PARTIAL_COMPLETE    = livePin("paymentRuns", "RUN_PARTIAL_COMPLETE");

export const SUPER_ADMIN = new Proxy({}, {
  get: (_, prop) => {
    const id = STORE.manifest?.adminUsers?.SUPER_ADMIN || "adm_001";
    const rec = STORE.adminUsers.find(a => a.id === id) || STORE.adminUsers[0];
    return rec?.[prop];
  },
});

// ── Current-admin session ─────────────────────────────────────────────────
// `SUPER_ADMIN` is misnamed — it's actually the *currently signed-in admin*.
// AdminLoginPage pre-selects a role, and on MFA success we re-pin this so
// every admin-shell read (header, sidebar footer, audit actor) flows through
// the chosen admin. Sign-out restores the default Super Admin (Todd).
const DEFAULT_ADMIN_ID = "adm_001";

export function signInAsAdmin(adminId) {
  if (!adminId) throw new Error("signInAsAdmin: adminId required");
  mutate(draft => {
    draft.manifest = {
      ...draft.manifest,
      adminUsers: { ...draft.manifest.adminUsers, SUPER_ADMIN: adminId },
    };
    appendAuditEvent(draft, {
      action:      "AdminSignedIn",
      entityType:  "AdminUser",
      entityId:    adminId,
      description: `Admin session started`,
      actorId:     adminId,
    });
  });
}

export function signOutAdmin() {
  mutate(draft => {
    draft.manifest = {
      ...draft.manifest,
      adminUsers: { ...draft.manifest.adminUsers, SUPER_ADMIN: DEFAULT_ADMIN_ID },
    };
  });
}

// ADM-08 cards — pinned by manifest IDs.
export const ADM08_CARDS = {
  get length() {
    const ids = STORE.manifest?.cards?.ADM08_CARD_IDS || [];
    return STORE.cards.filter(c => ids.includes(c.id)).length;
  },
};

// ─── Lookup helpers (live) ──────────────────────────────────────────────────
export const getMemberById         = (id) => STORE.members.find(m => m.id === id);
export const getDealById           = (id) => STORE.deals.find(d => d.id === id);
export const getBrandById          = (id) => STORE.brands.find(b => b.id === id);
export const getApplicationById    = (id) => STORE.applications.find(a => a.id === id);
export const getReservationById    = (id) => STORE.reservations.find(r => r.id === id);
export const getSubmissionById     = (id) => STORE.submissions.find(s => s.id === id);
export const getCardById           = (id) => STORE.cards.find(c => c.id === id);
export const getPaymentItemById    = (id) => STORE.paymentItems.find(p => p.id === id);
export const getPaymentRunById     = (id) => STORE.paymentRuns.find(r => r.id === id);
export const getAdminById          = (id) => STORE.adminUsers.find(a => a.id === id);
export const getValidationRuleById = (id) => STORE.validationRules.find(r => r.id === id);

export const getActiveDeals          = () => STORE.deals.filter(d => d.status === "Published");
export const getReservationsByMember = (memberId) => STORE.reservations.filter(r => r.memberId === memberId);
export const getSubmissionsByMember  = (memberId) => STORE.submissions.filter(s => s.memberId === memberId);
export const getSubmissionsByStatus  = (status)   => STORE.submissions.filter(s => s.status === status);
export const getPaymentItemsByMember = (memberId) => STORE.paymentItems.filter(p => p.memberId === memberId);
export const getPaymentItemsByRun    = (runId)    => STORE.paymentItems.filter(p => p.runId === runId);
export const getCardsBySubmission    = (subId)    => STORE.cards.filter(c => c.submissionId === subId);
export const getReservationsByDeal   = (dealId)   => STORE.reservations.filter(r => r.dealId === dealId);
export const getSubmissionsByDeal    = (dealId)   => STORE.submissions.filter(s => s.dealId === dealId);
export const getApplicationsByStatus = (status)   => STORE.applications.filter(a => a.status === status);

export const getAuditEventsByActor   = (actorId)  => STORE.auditEvents.filter(e => e.actorId === actorId);
export const getAuditEventsByEntity  = (type, id) => STORE.auditEvents.filter(e => e.entityType === type && e.entityId === id);

export function getMemberWithRelations(memberId) {
  const member = getMemberById(memberId);
  if (!member) return null;
  return {
    ...member,
    reservations: getReservationsByMember(memberId),
    submissions:  getSubmissionsByMember(memberId),
    payments:     getPaymentItemsByMember(memberId),
    auditTrail:   getAuditEventsByActor(memberId),
  };
}

export function getDealWithRelations(dealId) {
  const deal = getDealById(dealId);
  if (!deal) return null;
  return {
    ...deal,
    brand:           getBrandById(deal.brandId),
    reservations:    getReservationsByDeal(dealId),
    submissions:     getSubmissionsByDeal(dealId),
    validationRules: (deal.validationRuleIds || []).map(getValidationRuleById).filter(Boolean),
    creator:         getAdminById(deal.createdBy),
  };
}

export function getSubmissionWithRelations(subId) {
  const submission = getSubmissionById(subId);
  if (!submission) return null;
  return {
    ...submission,
    member: getMemberById(submission.memberId),
    deal:   getDealById(submission.dealId),
    brand:  getBrandById(submission.brandId),
    cards:  getCardsBySubmission(subId),
  };
}

export function getPaymentRunWithRelations(runId) {
  const run = getPaymentRunById(runId);
  if (!run) return null;
  const items = getPaymentItemsByRun(runId);
  return {
    ...run,
    items,
    members:   items.map(it => getMemberById(it.memberId)).filter(Boolean),
    generator: getAdminById(run.generatedBy),
  };
}

// ─── Dashboard aggregates ───────────────────────────────────────────────────
function formatUsd(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n || 0);
}

export function getMemberDashboardStats(memberId = "mbr_001") {
  const r = getReservationsByMember(memberId);
  const s = getSubmissionsByMember(memberId);
  const p = getPaymentItemsByMember(memberId);
  const activeReservations = r.filter(x => x.status === "Active" || x.status === "SubmittedPartial");
  const pendingBalance = p.filter(x => x.status === "Scheduled" || x.status === "Sent" || x.status === "Eligible").reduce((acc, x) => acc + (x.amount || 0), 0);
  const lifetime = p.filter(x => x.status === "Completed").reduce((acc, x) => acc + (x.amount || 0), 0);

  const recentActivity = [
    ...r.slice(0, 4).map(res => ({
      type:        "reservation",
      at:          res.createdAt,
      action:      "Reserved",
      description: `Reserved ${formatUsd(res.faceValueReserved)} on ${getDealById(res.dealId)?.name || "—"}`,
      icon:        "Bookmark",
    })),
    ...s.slice(0, 3).map(sub => ({
      type:        "submission",
      at:          sub.submittedAt,
      action:      "Submitted",
      description: `Submitted ${sub.cardCount} cards (${formatUsd(sub.totalFaceValue)} face value)`,
      icon:        "FileText",
    })),
    ...p.filter(x => x.status === "Completed").slice(0, 2).map(pay => ({
      type:        "payment",
      at:          pay.completedAt,
      action:      "Paid",
      description: `Received ${formatUsd(pay.amount)}${pay.refNumber ? ` — ref ${pay.refNumber}` : ""}`,
      icon:        "Wallet",
    })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 8);

  return {
    activeReservationsCount:       activeReservations.length,
    activeReservationsAcrossDeals: new Set(activeReservations.map(x => x.dealId)).size,
    pendingBalance,
    lifetimeEarnings:              lifetime,
    availableCapacity:             Math.max(0, 5000 - activeReservations.reduce((acc, x) => acc + (x.faceValueReserved || 0), 0)),
    recentActivity,
  };
}

export function getAdminDashboardStats() {
  const flagged = STORE.submissions.filter(s =>
    s.status === "FlaggedFormat" || s.status === "FlaggedDuplicate" || s.status === "AwaitingMemberResponse" || s.status === "Flagged"
  ).length;
  return {
    activeDealsCount:         STORE.deals.filter(d => d.status === "Published").length,
    pendingApplicationsCount: STORE.applications.filter(a => a.status === "PendingReview").length,
    flaggedSubmissionsCount:  flagged,
    paymentRunsReady:         STORE.paymentRuns.filter(r => r.status === "Generated").length,
    activeMembersCount:       STORE.members.filter(m => m.status === "Active").length,
    totalCapDeployed:         STORE.deals.filter(d => d.status === "Published").reduce((acc, d) => acc + (d.capTotal || 0), 0),
    totalCapUsed:             STORE.deals.filter(d => d.status === "Published").reduce((acc, d) => acc + (d.capUsed  || 0), 0),
    recentActivity:           STORE.auditEvents.slice(0, 10),
  };
}

// ─── Time series (deterministic) ────────────────────────────────────────────
const TODAY = new Date("2026-05-09T00:00:00-04:00");

export function getCardsProcessedSeries() {
  const out = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(TODAY.getTime() - i * 86_400_000);
    const base  = 80 + Math.sin(i * 0.4) * 30;
    const noise = Math.sin(i * 13.37) * 14;
    const cards = Math.max(20, Math.round(base + noise));
    out.push({ date: d.toISOString().slice(0, 10), label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }), cards });
  }
  return out;
}

export function getPaymentVolumeSeries() {
  const out = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(TODAY.getTime() - i * 7 * 86_400_000);
    const base   = 18000 + Math.cos(i * 0.6) * 6000;
    const noise  = Math.sin(i * 7.91) * 3500;
    const volume = Math.max(6000, Math.round(base + noise));
    out.push({ week: `W${12 - i}`, label: `Week of ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`, volume });
  }
  return out;
}

// ─── Trust indicators + static copy ─────────────────────────────────────────
export const trustIndicators = {
  activeMembers:      248,
  totalPaidOut:       1_240_500,
  brands:             14,
  averageRate:        92.4,
  approvedLast30d:    2480,
  fastestPayoutHours: 38,
};

export const faqContent = [
  { q: "What is Gift Card Buyers Club?",         a: "A members-only buying group that pays cash for unused gift cards from major retailers. We pool member supply to negotiate higher rates than any individual could get alone." },
  { q: "How is this different from a reseller?", a: "We're a closed, vetted group. Every member is identity-verified. We never resell anonymously — our supply chain is auditable end-to-end." },
  { q: "Why do I need to apply?",                a: "Closed membership protects existing members from saturation and protects us from high-risk supply. Approval is typically within 2 business days." },
  { q: "How fast do I get paid?",                a: "Once your cards are approved, payment is sent on the deal's payment terms (typically 5–10 days from approval) by ACH to the bank account on your file." },
  { q: "Are my card numbers safe?",              a: "Card numbers and bank account numbers are encrypted at rest with AWS KMS envelope encryption. We display only the last four digits anywhere in the app." },
  { q: "What happens to my data if I leave?",    a: "On account deactivation we retain only what we're legally required to (transaction records for tax purposes) and hard-delete the rest within 30 days." },
  { q: "Do you charge a fee?",                   a: "There are no membership fees, listing fees, or platform fees. We make our money on the spread between what we pay you and what we sell to bulk buyers." },
  { q: "Why ACH and not PayPal or Venmo?",       a: "ACH is the most cost-effective rail for large recurring transfers and avoids the 1099-K thresholds and fee skim of consumer apps." },
  { q: "What's the minimum balance to cash out?",a: "There's no minimum. Each approved card creates a payable item that's included in the next scheduled payment run." },
  { q: "Can I refer friends?",                   a: "Yes — referrals are noted on the application form. We don't run a paid referral programme but referrals from existing members move higher in the review queue." },
];

// Per-deal default FAQ (used when a deal doesn't set `faqOverride`).
// These are oriented around a reservation lifecycle: reserve → submit → approve → pay.
export const dealDefaultFAQ = [
  { q: "When do I get paid?",
    a: "Once your cards are approved, payment is sent ACH on the deal's payment terms (shown above). Most deals run 5–10 days from approval to ACH initiation, then 1–2 business days for the deposit to land." },
  { q: "What if a card is rejected?",
    a: "Rejected cards are flagged with a reason (e.g., format invalid, duplicate, pre-redeemed). You won't be paid for rejected cards, but they don't count against your reservation — you can submit more cards to fill it." },
  { q: "Can I cancel my reservation?",
    a: "Yes — until you submit cards against it. After you've submitted cards, those approved cards will still be paid out, but you can cancel the unused portion of the reservation." },
  { q: "What if I miss the deadline?",
    a: "Reservations expire at the deadline. Any cards you submitted before the deadline are still processed and paid normally — only the unused reservation capacity is released back to the pool." },
  { q: "What happens if my bank rejects the ACH?",
    a: "An ACH return triggers a manual review. We'll contact you to update banking details, then re-attempt the payment on the next run. Returns are recorded on your account but don't affect your member status." },
  { q: "Why might the rate change?",
    a: "Rates are locked at reservation time. If we raise a deal's rate later (a 'retroactive bump'), your existing reservation is upgraded automatically. We never lower a published rate retroactively." },
];

export const systemConfig = {
  hero: {
    headline: "Sell unused gift cards. Get paid in days.",
    sub:      "A members-only buying group with vetted supply, transparent rates, and US-bank ACH payouts.",
  },
  steps: [
    { n: 1, title: "Apply",    body: "We verify your identity and source-of-cards in 2 business days." },
    { n: 2, title: "Reserve",  body: "Browse live deals, reserve capacity at the listed rate." },
    { n: 3, title: "Submit",   body: "Paste your card numbers and PINs. We validate and approve." },
    { n: 4, title: "Get paid", body: "ACH straight to your bank on the deal's payment terms." },
  ],
  supportEmail:   "support@giftcardbuyersclub.com",
  termsVersion:   "v1.4",
  privacyVersion: "v1.2",
  defaultDeal:    { paymentTermsDays: 7, perMemberCapPct: 25, autoCloseOnCap: true },
  reviewWorkflow: { requireDoubleApproval: false, coolingOffDays: 90 },
};

// ════════════════════════════════════════════════════════════════════════════
// MUTATORS — every status change goes through these so localStorage stays
// in sync, all subscribed components re-render, and an audit event is logged.
// ════════════════════════════════════════════════════════════════════════════

export function updateApplicationStatus(appId, status, extras = {}) {
  guardAction(status === "Rejected" ? "applications.reject" : "applications.approve");
  mutate(draft => {
    draft.applications = draft.applications.map(a =>
      a.id === appId ? { ...a, status, ...extras, reviewedAt: new Date().toISOString() } : a
    );
    appendAuditEvent(draft, {
      action:      status === "Rejected" ? "ApplicationRejected" : "ApplicationApproved",
      entityType:  "Application",
      entityId:    appId,
      description: status === "Rejected"
        ? `Rejected application ${appId}${extras.rejectReason ? `: ${extras.rejectReason}` : ""}`
        : `Approved application ${appId} — moved to ${status}`,
    });
  });
}

export function updateMemberStatus(memberId, status, reason = null) {
  guardAction(status === "Suspended" ? "members.suspend"
            : status === "Deactivated" ? "members.suspend"
            : "members.reactivate");
  mutate(draft => {
    draft.members = draft.members.map(m =>
      m.id === memberId ? {
        ...m,
        status,
        suspendedAt:   status === "Suspended"   ? new Date().toISOString() : m.suspendedAt,
        suspendReason: status === "Suspended"   ? (reason || m.suspendReason) : m.suspendReason,
        deactivatedAt: status === "Deactivated" ? new Date().toISOString() : m.deactivatedAt,
      } : m
    );
    appendAuditEvent(draft, {
      action:      status === "Suspended" ? "MemberSuspended" : status === "Deactivated" ? "MemberDeactivated" : "MemberReactivated",
      entityType:  "Member",
      entityId:    memberId,
      description: reason ? `${status}: ${reason}` : `Member status → ${status}`,
    });
  });
}

// Recompute reservation aggregates (cardsSubmitted/Approved + faceValueSubmitted/Approved)
// from the live cards array. Call this inside a mutate() draft whenever any
// card's status changes. Single source of truth = the cards table.
function deriveReservationAggregates(draft, reservationIds) {
  const ids = Array.isArray(reservationIds) ? reservationIds : [reservationIds];
  const dedup = [...new Set(ids.filter(Boolean))];
  draft.reservations = draft.reservations.map(r => {
    if (!dedup.includes(r.id)) return r;
    const rc = draft.cards.filter(c => c.reservationId === r.id);
    const submittedCount = rc.length;
    const approvedCount  = rc.filter(c => c.status === "Approved").length;
    const submittedFace  = rc.reduce((s, c) => s + (c.faceValue || 0), 0);
    const approvedFace   = rc.filter(c => c.status === "Approved").reduce((s, c) => s + (c.faceValue || 0), 0);
    // Status transition rules:
    // • Cancelled / ExpiredReleased are terminal — never overwrite.
    // • Active → SubmittedPartial when any cards arrive.
    // • Any non-final state → SubmittedFinal when approved face hits target.
    let nextStatus = r.status;
    const isTerminal = r.status === "Cancelled" || r.status === "ExpiredReleased";
    if (!isTerminal) {
      if (approvedFace >= r.faceValueReserved && submittedCount > 0) nextStatus = "SubmittedFinal";
      else if (submittedCount > 0)                                    nextStatus = "SubmittedPartial";
      else                                                            nextStatus = "Active";
    }
    return { ...r,
      cardsSubmitted:     submittedCount,
      cardsApproved:      approvedCount,
      faceValueSubmitted: submittedFace,
      faceValueApproved:  approvedFace,
      status:             nextStatus,
    };
  });
}

// Build an Eligible paymentItem from a newly-approved card. Returns null if a
// non-terminal paymentItem already references this card (idempotent), or if
// the card can't be priced (no deal/brand/rate resolvable). Pushes the new
// item into draft.paymentItems and emits a `PaymentItemEligible` audit event.
function createEligiblePaymentItemForCard(draft, card) {
  if (!card || card.status !== "Approved") return null;
  // Idempotency: don't double-emit if an active item already covers this card.
  const exists = draft.paymentItems.some(p =>
    (p.cardIds || []).includes(card.id) &&
    p.status !== "Cancelled" && p.status !== "Returned" && p.status !== "Refunded");
  if (exists) return null;
  const reservation = draft.reservations.find(r => r.id === card.reservationId);
  const deal = reservation ? draft.deals.find(d => d.id === reservation.dealId) : null;
  if (!deal) return null;
  const brandId = reservation?.brandId || (deal.brandConfigs?.[0]?.brandId);
  const cfg = (deal.brandConfigs || []).find(bc => bc.brandId === brandId) || deal.brandConfigs?.[0];
  const rate = cfg?.rate ?? deal.rate ?? 0;
  if (!rate || !card.faceValue) return null;
  const amount = Math.round(card.faceValue * rate) / 100;
  const member = draft.members.find(m => m.id === card.memberId);
  const bankLast4 = member?.bankAccount?.accountLast4 || member?.accountLast4 || null;
  const submission = draft.submissions.find(s => s.id === card.submissionId);
  const item = {
    id:               `pay_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    memberId:         card.memberId,
    runId:            null,
    submissionId:     submission?.id || null,
    dealId:           deal.id,
    brandId,
    amount,
    cardCount:        1,
    cardIds:          [card.id],
    status:           "Eligible",
    scheduledFor:     null,
    sentAt:           null,
    completedAt:      null,
    refNumber:        null,
    bankLast4,
    bankAccountLast4: bankLast4,
    returnReason:     null,
    returnedAt:       null,
  };
  draft.paymentItems = [...draft.paymentItems, item];
  appendAuditEvent(draft, {
    action:      "PaymentItemEligible",
    entityType:  "PaymentItem",
    entityId:    item.id,
    description: `Card ${card.id} approved → paymentItem ${item.id} eligible for ${item.amount.toFixed(2)}`,
  });
  return item;
}

// Recompute submission lifecycle status from its cards. Called after every
// admin card-status mutation. Rules:
//   • Any card still Pending / Flagged*  → submission stays "PendingReview"
//   • Any card "AwaitingMemberResponse"  → submission "AwaitingMemberResponse"
//   • Every card terminal (Approved / Rejected):
//       - all Rejected           → "Rejected"
//       - everything else        → "Completed" (had at least one admin touch)
// Stamps reviewedAt / reviewedBy on terminal transitions.
function recomputeSubmissionStatus(draft, submissionId) {
  const sub = draft.submissions.find(s => s.id === submissionId);
  if (!sub) return;
  // Auto-approved submissions (status === "Approved" from submitCards()) stay
  // there forever — they never needed admin review.
  if (sub.status === "Approved" && !sub.flaggedCount) return;

  const subCards = draft.cards.filter(c => c.submissionId === submissionId);
  if (subCards.length === 0) return;

  const pending = subCards.some(c =>
    c.status === "Pending"
    || c.status === "FlaggedFormat"
    || c.status === "FlaggedDuplicate"
    || c.status === "FlaggedAmount");
  const awaiting = subCards.some(c => c.status === "AwaitingMemberResponse");
  const approved = subCards.filter(c => c.status === "Approved").length;
  const rejected = subCards.filter(c => c.status === "Rejected").length;

  let nextStatus = sub.status;
  if (pending)                              nextStatus = "PendingReview";
  else if (awaiting)                        nextStatus = "AwaitingMemberResponse";
  else if (rejected === subCards.length)    nextStatus = "Rejected";
  else                                      nextStatus = "Completed";

  if (nextStatus === sub.status) return;

  const adminId = STORE.manifest?.adminUsers?.SUPER_ADMIN || "adm_001";
  const now = new Date().toISOString();
  const isTerminal = nextStatus === "Completed" || nextStatus === "Rejected";
  draft.submissions = draft.submissions.map(s => s.id === submissionId
    ? { ...s,
        status: nextStatus,
        reviewedAt:        isTerminal ? now      : s.reviewedAt,
        reviewedBy:        isTerminal ? adminId  : s.reviewedBy,
        approvedFaceValue: subCards.filter(c => c.status === "Approved").reduce((sum, c) => sum + (c.faceValue || 0), 0),
      }
    : s);
  if (isTerminal) {
    appendAuditEvent(draft, {
      action:      `Submission${nextStatus}`,
      entityType:  "Submission",
      entityId:    submissionId,
      description: `Submission ${submissionId} closed → ${nextStatus} (${approved} approved · ${rejected} rejected)`,
    });
  }
}

export function updateCardStatus(cardId, status, options = {}) {
  guardAction(status === "Approved" ? "submissions.approve"
            : status === "Rejected" ? "submissions.reject"
            : "submissions.request");
  const overrideReason = options.reason || null;
  mutate(draft => {
    const before = draft.cards.find(c => c.id === cardId);
    const wasApproved = before?.status === "Approved";
    const wasFlagged = before?.status?.startsWith?.("Flagged");
    draft.cards = draft.cards.map(c => (c.id === cardId
      ? { ...c,
          status,
          approvedAt: status === "Approved" ? new Date().toISOString() : c.approvedAt,
          overrideReason: overrideReason || c.overrideReason || null,
          overriddenAt:   overrideReason ? new Date().toISOString() : c.overriddenAt,
        }
      : c));
    if (before?.reservationId) deriveReservationAggregates(draft, before.reservationId);
    if (before?.submissionId) recomputeSubmissionStatus(draft, before.submissionId);
    if (status === "Approved" && !wasApproved) {
      const card = draft.cards.find(c => c.id === cardId);
      createEligiblePaymentItemForCard(draft, card);
    }
    appendAuditEvent(draft, {
      action:      wasFlagged && overrideReason ? `Card${status}Override` : `Card${status}`,
      entityType:  "Card",
      entityId:    cardId,
      description: overrideReason
        ? `Card ${cardId} → ${status} (override: ${overrideReason})`
        : `Card ${cardId} → ${status}`,
    });
  });
}

export function bulkUpdateCardsStatus(cardIds, status) {
  guardAction(status === "Approved" ? "submissions.approve"
            : status === "Rejected" ? "submissions.reject"
            : "submissions.request");
  mutate(draft => {
    const set = new Set(cardIds);
    const beforeApproved = new Set(
      draft.cards.filter(c => set.has(c.id) && c.status === "Approved").map(c => c.id));
    const affectedRes = [...new Set(draft.cards.filter(c => set.has(c.id)).map(c => c.reservationId))];
    const affectedSubs = [...new Set(draft.cards.filter(c => set.has(c.id)).map(c => c.submissionId))];
    const now = new Date().toISOString();
    draft.cards = draft.cards.map(c => (set.has(c.id)
      ? { ...c, status, approvedAt: status === "Approved" ? now : c.approvedAt }
      : c));
    deriveReservationAggregates(draft, affectedRes);
    affectedSubs.forEach(subId => subId && recomputeSubmissionStatus(draft, subId));
    if (status === "Approved") {
      draft.cards
        .filter(c => set.has(c.id) && !beforeApproved.has(c.id))
        .forEach(card => createEligiblePaymentItemForCard(draft, card));
    }
    appendAuditEvent(draft, {
      action:      `CardsBulk${status}`,
      entityType:  "Card",
      entityId:    cardIds[0],
      description: `Bulk-${status.toLowerCase()} ${cardIds.length} cards`,
    });
  });
}

export function updateSubmissionStatus(subId, status, flagReason = null) {
  guardAction(status === "Approved" ? "submissions.approve"
            : status === "Rejected" ? "submissions.reject"
            : "submissions.request");
  mutate(draft => {
    draft.submissions = draft.submissions.map(s =>
      s.id === subId ? { ...s, status, flagReason: flagReason ?? s.flagReason, reviewedAt: new Date().toISOString() } : s
    );
    appendAuditEvent(draft, {
      action:      `Submission${status}`,
      entityType:  "Submission",
      entityId:    subId,
      description: flagReason ? `Submission ${subId} → ${status}: ${flagReason}` : `Submission ${subId} → ${status}`,
    });
  });
}

export function updateDealStatus(dealId, status, extras = {}) {
  guardAction(status === "Published" || status === "Closed" || status === "Paused" ? "deals.publish" : "deals.edit");
  mutate(draft => {
    draft.deals = draft.deals.map(d => (d.id === dealId ? { ...d, status, ...extras } : d));
    appendAuditEvent(draft, {
      action:      `Deal${status}`,
      entityType:  "Deal",
      entityId:    dealId,
      description: `Deal ${dealId} → ${status}`,
    });
  });
}

// Helpers for brandConfigs[] aggregation.
function aggregateFromBrandConfigs(brandConfigs) {
  const primaryCfg = brandConfigs.find(c => c.isPrimary) || brandConfigs[0];
  return {
    brandId:      primaryCfg.brandId,
    brandIds:     brandConfigs.map(c => c.brandId),
    isMultiBrand: brandConfigs.length > 1,
    ratesUniform: brandConfigs.every(c => c.rate === primaryCfg.rate),
    rate:         primaryCfg.rate,
    margin:       Math.max(0, +(100 - primaryCfg.rate).toFixed(2)),
    capTotal:     brandConfigs.reduce((a, c) => a + (Number(c.capTotal) || 0), 0),
    capUsed:      brandConfigs.reduce((a, c) => a + (Number(c.capUsed)  || 0), 0),
    minFace:      Math.min(...brandConfigs.map(c => Number(c.minFace) || 0)),
    maxFace:      Math.max(...brandConfigs.map(c => Number(c.maxFace) || 0)),
    perMemberCap: primaryCfg.perMemberCap,
  };
}

export function updateDeal(dealId, patch) {
  // Publishing emits a status change to "Published" → tighter check.
  guardAction(patch?.status === "Published" ? "deals.publish" : "deals.edit");
  mutate(draft => {
    // Capture per-brand rate BEFORE the mutation so we can audit per-brand changes.
    const before = draft.deals.find(d => d.id === dealId);
    const beforeRates = before?.brandConfigs
      ? Object.fromEntries(before.brandConfigs.map(c => [c.brandId, c.rate]))
      : {};

    draft.deals = draft.deals.map(d => {
      if (d.id !== dealId) return d;
      let next = { ...d, ...patch };
      // If brandConfigs were patched (or brandIds changed), recompute aggregates.
      if (Array.isArray(next.brandConfigs) && next.brandConfigs.length) {
        // Ensure exactly one primary.
        if (!next.brandConfigs.some(c => c.isPrimary)) {
          next.brandConfigs = next.brandConfigs.map((c, i) => ({ ...c, isPrimary: i === 0 }));
        }
        next = { ...next, ...aggregateFromBrandConfigs(next.brandConfigs) };
      }
      return next;
    });

    // Emit one DealRateChanged audit event PER brand whose rate moved. This is
    // the data trail compliance needs when a retroactive bump happens.
    const after = draft.deals.find(d => d.id === dealId);
    (after?.brandConfigs || []).forEach(c => {
      const prev = beforeRates[c.brandId];
      if (prev != null && prev !== c.rate) {
        const brandName = draft.brands.find(b => b.id === c.brandId)?.name || c.brandId;
        appendAuditEvent(draft, {
          action:      "DealRateChanged",
          entityType:  "Deal",
          entityId:    dealId,
          description: `${brandName} rate changed ${prev}% → ${c.rate}% on ${after.name}`,
        });
      }
    });

    appendAuditEvent(draft, {
      action:      "DealUpdated",
      entityType:  "Deal",
      entityId:    dealId,
      description: `Updated deal ${dealId}`,
    });
  });
}

export function createDeal(seed) {
  guardAction("deals.create");
  const id = seed.id || `deal_${Math.random().toString(36).slice(2, 9)}`;
  // Build brandConfigs from seed; if a flat single brand+rate was passed, wrap it.
  let brandConfigs;
  if (Array.isArray(seed.brandConfigs) && seed.brandConfigs.length) {
    brandConfigs = seed.brandConfigs.map((c, i) => ({
      brandId:      c.brandId,
      isPrimary:    c.isPrimary ?? (i === 0),
      rate:         Number(c.rate) || 92,
      capTotal:     Number(c.capTotal) || 10000,
      capUsed:      0,
      minFace:      Number(c.minFace) || 25,
      maxFace:      Number(c.maxFace) || 500,
      perMemberCap: Number(c.perMemberCap) || Math.round((Number(c.capTotal) || 10000) * 0.25),
    }));
  } else {
    const primary = seed.brandId || "";
    brandConfigs = [{
      brandId:      primary,
      isPrimary:    true,
      rate:         Number(seed.rate) || 92,
      capTotal:     Number(seed.capTotal) || 10000,
      capUsed:      0,
      minFace:      Number(seed.minFace) || 25,
      maxFace:      Number(seed.maxFace) || 500,
      perMemberCap: Number(seed.perMemberCap) || Math.round((Number(seed.capTotal) || 10000) * 0.25),
    }];
  }
  if (!brandConfigs.some(c => c.isPrimary)) brandConfigs[0].isPrimary = true;
  const agg = aggregateFromBrandConfigs(brandConfigs);
  const deal = {
    id,
    name:                seed.name || "Untitled deal",
    ...agg,
    brandConfigs,
    status:              seed.status || "Draft",
    openedAt:            seed.openedAt || new Date().toISOString(),
    deadline:            seed.deadline || "",
    paymentTerms:        seed.paymentTerms || "Net 7 days from approval",
    validationRuleIds:   seed.validationRuleIds || [],
    publicNote:          seed.publicNote || "",
    whyBuying:           seed.whyBuying || "",
    faqOverride:         Array.isArray(seed.faqOverride) ? seed.faqOverride : null,
    isFeatured:          !!seed.isFeatured,
    internalNote:        seed.internalNote || "",
    createdBy:           seed.createdBy || "adm_001",
    createdAt:           new Date().toISOString(),
    publishedAt:         seed.status === "Published" ? new Date().toISOString() : null,
    reservationsCount:   0,
    cardsSubmittedCount: 0,
    cardsApprovedCount:  0,
  };
  mutate(draft => {
    draft.deals = [deal, ...draft.deals];
    appendAuditEvent(draft, {
      action:      "DealCreated",
      entityType:  "Deal",
      entityId:    id,
      description: `Created deal ${deal.name} (${deal.status})`,
    });
  });
  return id;
}

// Generate a payment run by sweeping all Eligible items (member-approved but
// not yet bundled) into a new run. Optional `cutoffISO` filters items whose
// most-recent card approvedAt is on/before the cutoff. Flips swept items
// Eligible → Scheduled and creates the paymentRun in status "Generated".
export function generatePaymentRun(cutoffISO = null) {
  guardAction("payments.generateRun");
  let createdRunId = null;
  let sweptCount = 0;
  let sweptTotal = 0;
  mutate(draft => {
    const cardsById = Object.fromEntries(draft.cards.map(c => [c.id, c]));
    const cutoff = cutoffISO ? new Date(cutoffISO).getTime() : null;
    const eligible = draft.paymentItems.filter(p => {
      if (p.status !== "Eligible") return false;
      if (!cutoff) return true;
      const card = cardsById[(p.cardIds || [])[0]];
      const ts = card?.approvedAt ? new Date(card.approvedAt).getTime() : null;
      return ts ? ts <= cutoff : true;
    });
    if (eligible.length === 0) return;
    const eligibleIds = new Set(eligible.map(p => p.id));
    const memberCount = new Set(eligible.map(p => p.memberId)).size;
    const total = eligible.reduce((s, p) => s + (p.amount || 0), 0);
    const adminId = STORE.manifest?.adminUsers?.SUPER_ADMIN || "adm_001";
    const now = new Date().toISOString();
    const id = `run_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;
    createdRunId = id;
    sweptCount = eligible.length;
    sweptTotal = total;
    const run = {
      id,
      generatedAt:    now,
      generatedBy:    adminId,
      cutoffDate:     cutoffISO || now,
      memberCount,
      itemCount:      eligible.length,
      itemIds:        eligible.map(p => p.id),
      totalAmount:    total,
      status:         "Generated",
      exportedAt:     null,
      exportedBy:     null,
      exportFormat:   null,
      csvDownloads:   0,
      nachaDownloads: 0,
      notes:          null,
    };
    draft.paymentRuns = [run, ...draft.paymentRuns];
    draft.paymentItems = draft.paymentItems.map(p => (
      eligibleIds.has(p.id)
        ? { ...p, status: "Scheduled", runId: id, scheduledFor: now }
        : p
    ));
    appendAuditEvent(draft, {
      action:      "PaymentRunGenerated",
      entityType:  "PaymentRun",
      entityId:    id,
      description: `Generated run ${id} with ${eligible.length} items totaling $${total.toFixed(2)}`,
    });
  });
  return { runId: createdRunId, count: sweptCount, total: sweptTotal };
}

export function markPaymentRunSent(runId) {
  guardAction("payments.markSent");
  mutate(draft => {
    draft.paymentRuns = draft.paymentRuns.map(r =>
      r.id === runId ? { ...r, status: "Sent", exportedAt: new Date().toISOString(), nachaDownloads: (r.nachaDownloads || 0) + 1 } : r
    );
    draft.paymentItems = draft.paymentItems.map(p =>
      p.runId === runId && (p.status === "Scheduled" || p.status === "Eligible") ? { ...p, status: "Sent", sentAt: new Date().toISOString() } : p
    );
    appendAuditEvent(draft, {
      action:       "PaymentRunSent",
      entityType:   "PaymentRun",
      entityId:     runId,
      description:  `Exported and sent payment run ${runId}`,
      isDecryption: true,
    });
  });
}

export function markPaymentRunDecrypted(runId) {
  guardAction("payments.export");
  mutate(draft => {
    appendAuditEvent(draft, {
      action:       "PaymentRunDecryptBankAccounts",
      entityType:   "PaymentRun",
      entityId:     runId,
      description:  `Decrypted bank accounts to export run ${runId}`,
      isDecryption: true,
    });
  });
}

export function markPaymentItemStatus(itemId, status, extras = {}) {
  guardAction(status === "Sent" ? "payments.markSent"
            : status === "Completed" ? "payments.markCompleted"
            : status === "Returned" ? "payments.markReturned"
            : status === "Refunded" ? "payments.markRefunded"
            : "payments.markSent");
  mutate(draft => {
    draft.paymentItems = draft.paymentItems.map(p =>
      p.id === itemId ? { ...p, status, ...extras } : p
    );
    appendAuditEvent(draft, {
      action:      `PaymentItem${status}`,
      entityType:  "PaymentItem",
      entityId:    itemId,
      description: `Payment item ${itemId} → ${status}`,
    });
  });
}

// Bulk: mark every Sent item in a run as Completed (ACH settled in the bank).
// Also rolls the run's status to "Completed" once nothing non-terminal remains.
export function markPaymentRunCompleted(runId) {
  guardAction("payments.markCompleted");
  let flipped = 0;
  mutate(draft => {
    const now = new Date().toISOString();
    flipped = draft.paymentItems.filter(p => p.runId === runId && p.status === "Sent").length;
    draft.paymentItems = draft.paymentItems.map(p =>
      p.runId === runId && p.status === "Sent"
        ? { ...p, status: "Completed", completedAt: now }
        : p
    );
    // Roll the run status: if every item is terminal, flip to Completed.
    const runItems = draft.paymentItems.filter(p => p.runId === runId);
    const allTerminal = runItems.length > 0 && runItems.every(p =>
      p.status === "Completed" || p.status === "Returned" || p.status === "Refunded" || p.status === "Cancelled");
    const anyReturnedOrRefunded = runItems.some(p => p.status === "Returned" || p.status === "Refunded");
    if (allTerminal) {
      draft.paymentRuns = draft.paymentRuns.map(r =>
        r.id === runId
          ? { ...r, status: anyReturnedOrRefunded ? "CompletedPartial" : "Completed", completedAt: now }
          : r);
    }
    appendAuditEvent(draft, {
      action:      "PaymentRunMarkAllCompleted",
      entityType:  "PaymentRun",
      entityId:    runId,
      description: `Marked ${flipped} Sent items in run ${runId} as Completed`,
    });
  });
  return flipped;
}

// Cancel a payment item (e.g. wrong amount, card disputed before ACH).
// Terminal — funds were never released. Requires a reason for the audit trail.
export function cancelPaymentItem(itemId, reason) {
  guardAction("payments.cancel");
  mutate(draft => {
    draft.paymentItems = draft.paymentItems.map(p =>
      p.id === itemId ? {
        ...p,
        status:       "Cancelled",
        returnReason: reason || p.returnReason || "Cancelled by operator",
        returnedAt:   new Date().toISOString(),
      } : p
    );
    appendAuditEvent(draft, {
      action:      "PaymentItemCancelled",
      entityType:  "PaymentItem",
      entityId:    itemId,
      description: `Cancelled payment item ${itemId}${reason ? ` — ${reason}` : ""}`,
    });
  });
}

// Manual ACH return — admin records the return reason / R-code on a payment.
// Phase 1 is manual; no automation. Status flip + audit trail only.
export function markPaymentItemReturned(itemId, returnReason, note = null) {
  guardAction("payments.markReturned");
  mutate(draft => {
    draft.paymentItems = draft.paymentItems.map(p =>
      p.id === itemId ? {
        ...p,
        status:       "Returned",
        returnReason: returnReason || p.returnReason || "Unspecified",
        returnedAt:   new Date().toISOString(),
        notes:        note ?? p.notes,
      } : p
    );
    appendAuditEvent(draft, {
      action:      "PaymentItemReturned",
      entityType:  "PaymentItem",
      entityId:    itemId,
      description: `Manually marked returned${returnReason ? ` — ${returnReason}` : ""}`,
    });
  });
}

// Manual refund — admin records why a payment was refunded.
export function markPaymentItemRefunded(itemId, refundReason = null) {
  guardAction("payments.markRefunded");
  mutate(draft => {
    draft.paymentItems = draft.paymentItems.map(p =>
      p.id === itemId ? {
        ...p,
        status:       "Refunded",
        returnReason: refundReason || p.returnReason || "Member request",
        returnedAt:   new Date().toISOString(),
      } : p
    );
    appendAuditEvent(draft, {
      action:      "PaymentItemRefunded",
      entityType:  "PaymentItem",
      entityId:    itemId,
      description: `Manually marked refunded${refundReason ? ` — ${refundReason}` : ""}`,
    });
  });
}

// Bulk variants — same actions across many items.
export function bulkMarkPaymentItemsReturned(itemIds, returnReason) {
  guardAction("payments.markReturned");
  mutate(draft => {
    const set = new Set(itemIds);
    draft.paymentItems = draft.paymentItems.map(p =>
      set.has(p.id) ? { ...p, status: "Returned", returnReason: returnReason || "Unspecified", returnedAt: new Date().toISOString() } : p
    );
    appendAuditEvent(draft, {
      action:      "PaymentItemsBulkReturned",
      entityType:  "PaymentItem",
      entityId:    itemIds[0],
      description: `Bulk marked ${itemIds.length} payments returned (${returnReason || "Unspecified"})`,
    });
  });
}

export function bulkMarkPaymentItemsRefunded(itemIds, refundReason) {
  guardAction("payments.markRefunded");
  mutate(draft => {
    const set = new Set(itemIds);
    draft.paymentItems = draft.paymentItems.map(p =>
      set.has(p.id) ? { ...p, status: "Refunded", returnReason: refundReason || "Member request", returnedAt: new Date().toISOString() } : p
    );
    appendAuditEvent(draft, {
      action:      "PaymentItemsBulkRefunded",
      entityType:  "PaymentItem",
      entityId:    itemIds[0],
      description: `Bulk marked ${itemIds.length} payments refunded (${refundReason || "Member request"})`,
    });
  });
}

export function createReservation(memberId, dealId, faceValueReserved, brandId = null) {
  const deal = getDealById(dealId);
  // Resolve brand: explicit > deal's primary > first config.
  const resolvedBrandId = brandId
    || (deal?.brandConfigs?.find(c => c.isPrimary)?.brandId)
    || (deal?.brandId);
  const cfg = deal?.brandConfigs?.find(c => c.brandId === resolvedBrandId)
           || deal?.brandConfigs?.[0];

  // Enforce per-member cap defensively — UI also checks but we don't trust callers.
  // Sums non-cancelled prior reservations on this deal+brand for this member.
  const priorFace = STORE.reservations
    .filter(r => r.memberId === memberId
              && r.dealId === dealId
              && r.brandId === resolvedBrandId
              && r.status !== "Cancelled")
    .reduce((sum, r) => sum + (r.faceValueReserved || 0), 0);
  const perMemberCap = cfg?.perMemberCap || 0;
  if (perMemberCap > 0 && priorFace + faceValueReserved > perMemberCap) {
    const err = new Error(
      `Reservation rejected: $${(priorFace + faceValueReserved).toLocaleString()} exceeds per-member cap of $${perMemberCap.toLocaleString()} on ${resolvedBrandId} (already reserved $${priorFace.toLocaleString()}).`
    );
    err.name = "ReservationCapError";
    err.code = "PER_MEMBER_CAP_EXCEEDED";
    throw err;
  }
  // Also defend the brand pool cap.
  const remainingPool = (cfg?.capTotal || 0) - (cfg?.capUsed || 0);
  if (faceValueReserved > remainingPool) {
    const err = new Error(
      `Reservation rejected: $${faceValueReserved.toLocaleString()} exceeds remaining pool $${remainingPool.toLocaleString()} on ${resolvedBrandId}.`
    );
    err.name = "ReservationCapError";
    err.code = "POOL_CAP_EXCEEDED";
    throw err;
  }

  const rate = cfg?.rate ?? deal?.rate ?? 0;
  const expectedPayout = Math.round(faceValueReserved * (rate / 100) * 100) / 100;
  const id = `res_${Math.random().toString(36).slice(2, 9)}`;
  mutate(draft => {
    draft.reservations = [
      {
        id,
        memberId,
        dealId,
        brandId:            resolvedBrandId,
        faceValueReserved,
        expectedPayout,
        status:             "Active",
        cardsSubmitted:     0,
        cardsApproved:      0,
        faceValueSubmitted: 0,
        faceValueApproved:  0,
        createdAt:          new Date().toISOString(),
        reservedAt:         new Date().toISOString(),
        deadline:           deal?.deadline,
      },
      ...draft.reservations,
    ];
    // Decrement the SPECIFIC brand's capUsed, then re-aggregate.
    draft.deals = draft.deals.map(d => {
      if (d.id !== dealId) return d;
      const nextConfigs = (d.brandConfigs || []).map(c =>
        c.brandId === resolvedBrandId
          ? { ...c, capUsed: (c.capUsed || 0) + faceValueReserved }
          : c
      );
      return { ...d, brandConfigs: nextConfigs, ...aggregateFromBrandConfigs(nextConfigs) };
    });
    appendAuditEvent(draft, {
      action:      "ReservationCreated",
      entityType:  "Reservation",
      entityId:    id,
      description: `Reserved $${faceValueReserved.toLocaleString()} on ${deal?.name || dealId}${resolvedBrandId ? ` (${resolvedBrandId})` : ""}`,
      actorId:     memberId,
    });
  });
  return id;
}

export function cancelReservation(reservationId) {
  mutate(draft => {
    const res = draft.reservations.find(r => r.id === reservationId);
    draft.reservations = draft.reservations.map(r =>
      r.id === reservationId ? { ...r, status: "Cancelled", cancelledAt: new Date().toISOString() } : r
    );
    if (res) {
      draft.deals = draft.deals.map(d => {
        if (d.id !== res.dealId) return d;
        const nextConfigs = (d.brandConfigs || []).map(c =>
          c.brandId === res.brandId
            ? { ...c, capUsed: Math.max(0, (c.capUsed || 0) - (res.faceValueReserved || 0)) }
            : c
        );
        return { ...d, brandConfigs: nextConfigs, ...aggregateFromBrandConfigs(nextConfigs) };
      });
    }
    appendAuditEvent(draft, {
      action:      "ReservationCancelled",
      entityType:  "Reservation",
      entityId:    reservationId,
      description: `Cancelled reservation ${reservationId}`,
      actorId:     res?.memberId,
    });
  });
}

// ── Submit cards against a reservation ───────────────────────────────────
// Member action. The client-side parser has already validated format/length/PIN
// so every card arriving here is format-clean. This mutator does the server-
// side checks the client cannot: cross-submission and cross-member duplicates.
//   • Clean cards → Approved immediately + Eligible paymentItem emitted
//   • Duplicate-flagged cards → FlaggedDuplicate, queued for admin review
// Submission status: "Approved" if every card is clean, else "PendingReview".
export function submitCards(reservationId, parsedCards) {
  const valid = (parsedCards || []).filter(p => p.status === "valid"
                                              && p.cardNumber
                                              && p.pin
                                              && p.faceValue);
  if (!valid.length) {
    const err = new Error("submitCards: no valid cards in batch");
    err.name = "InvalidSubmissionError";
    throw err;
  }
  let submissionId;
  mutate(draft => {
    const reservation = draft.reservations.find(r => r.id === reservationId);
    if (!reservation) throw new Error(`submitCards: reservation ${reservationId} not found`);

    const now = new Date().toISOString();
    submissionId = `sub_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const totalFace = valid.reduce((s, c) => s + c.faceValue, 0);

    // Build a quick lookup: existing cards keyed by full number, scoped to the
    // same brand. (Different brands legitimately share digit patterns.)
    const existingByNumber = new Map();
    draft.cards.forEach(c => {
      if (!c.cardNumber) return;
      if (c.brandId && c.brandId !== reservation.brandId) return;
      if (!existingByNumber.has(c.cardNumber)) existingByNumber.set(c.cardNumber, []);
      existingByNumber.get(c.cardNumber).push(c);
    });

    // Intra-batch duplicate tracker.
    const intraBatchSeen = new Map();

    const classified = valid.map((c, i) => {
      const id = `card_${submissionId}_${i}`;
      const last4 = String(c.cardNumber).slice(-4);
      let flagReason = null;
      let duplicateOfCardId = null;
      let flagSeverity = null;

      if (intraBatchSeen.has(c.cardNumber)) {
        flagReason = `Duplicate of another card in this submission (ending ${last4})`;
        duplicateOfCardId = intraBatchSeen.get(c.cardNumber);
        flagSeverity = "intra-batch";
      } else {
        const matches = existingByNumber.get(c.cardNumber) || [];
        if (matches.length > 0) {
          const sameMember = matches.find(m => m.memberId === reservation.memberId);
          const otherMember = matches.find(m => m.memberId !== reservation.memberId);
          if (sameMember) {
            flagReason = `Member previously submitted this card (matched ${sameMember.id})`;
            duplicateOfCardId = sameMember.id;
            flagSeverity = "same-member";
          } else if (otherMember) {
            flagReason = `Cross-member duplicate of ${otherMember.id} (member ${otherMember.memberId})`;
            duplicateOfCardId = otherMember.id;
            flagSeverity = "cross-member";
          }
        }
      }
      intraBatchSeen.set(c.cardNumber, id);

      const isFlagged = !!flagReason;
      return {
        id,
        submissionId,
        reservationId:    reservation.id,
        memberId:         reservation.memberId,
        brandId:          reservation.brandId,
        cardNumber:       c.cardNumber,
        cardNumberLast4:  last4,
        pin:              c.pin,
        pinPresent:       true,
        faceValue:        c.faceValue,
        status:           isFlagged ? "FlaggedDuplicate" : "Approved",
        submittedAt:      now,
        approvedAt:       isFlagged ? null : now,
        flagReason,
        duplicateOfCardId,
        flagSeverity,
        parseConfidence:  c.parseConfidence ?? 1,
      };
    });

    draft.cards = [...classified, ...draft.cards];

    const flaggedCount = classified.filter(c => c.flagReason).length;
    const approvedCount = classified.length - flaggedCount;
    const submissionStatus = flaggedCount === 0 ? "Approved" : "PendingReview";
    const approvedFace = classified
      .filter(c => c.status === "Approved")
      .reduce((s, c) => s + (c.faceValue || 0), 0);

    const newSubmission = {
      id:                 submissionId,
      reservationId:      reservation.id,
      memberId:           reservation.memberId,
      dealId:             reservation.dealId,
      brandId:            reservation.brandId,
      rawText:            valid.map(c => `${c.cardNumber} PIN ${c.pin} $${c.faceValue}`).join("\n"),
      cardCount:          valid.length,
      totalFaceValue:     totalFace,
      approvedFaceValue:  approvedFace,
      status:             submissionStatus,
      submittedAt:        now,
      reviewedAt:         submissionStatus === "Approved" ? now : null,
      autoApprovedCount:  approvedCount,
      flaggedCount,
    };
    draft.submissions = [newSubmission, ...draft.submissions];

    // Emit Eligible paymentItems for every auto-approved card.
    classified.filter(c => c.status === "Approved").forEach(card => {
      createEligiblePaymentItemForCard(draft, card);
    });

    // Re-derive reservation aggregates from all cards (incl. these new ones).
    const allCardsForRes = draft.cards.filter(c => c.reservationId === reservation.id);
    const submittedCount = allCardsForRes.length;
    const approvedCountRes = allCardsForRes.filter(c => c.status === "Approved").length;
    const submittedFace  = allCardsForRes.reduce((s, c) => s + (c.faceValue || 0), 0);
    const approvedFaceRes = allCardsForRes.filter(c => c.status === "Approved").reduce((s, c) => s + (c.faceValue || 0), 0);

    const nextStatus = approvedFaceRes >= reservation.faceValueReserved && submittedCount > 0
      ? "SubmittedFinal"
      : reservation.status === "Active" ? "SubmittedPartial" : reservation.status;

    draft.reservations = draft.reservations.map(r => r.id === reservation.id
      ? { ...r,
          cardsSubmitted:      submittedCount,
          cardsApproved:       approvedCountRes,
          faceValueSubmitted:  submittedFace,
          faceValueApproved:   approvedFaceRes,
          status:              nextStatus }
      : r);

    appendAuditEvent(draft, {
      action:       "CardsSubmitted",
      entityType:   "Submission",
      entityId:     submissionId,
      description:  `Submitted ${valid.length} card${valid.length === 1 ? "" : "s"} ($${totalFace.toLocaleString()}) · ${approvedCount} auto-approved · ${flaggedCount} flagged for review`,
      actorId:      reservation.memberId,
    });
  });
  return submissionId;
}

export { resetStore };
